import React, { useState } from "react";
import axios from "axios";
import { format } from "date-fns";
import { Mail, Phone, Instagram, Facebook, MapPin, ArrowRight, Calendar as CalendarIcon, Loader2 } from "lucide-react";
import { CONTACT } from "../mock";
import { Reveal } from "../hooks/useReveal";
import { useToast } from "../hooks/use-toast";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Calendar } from "./ui/calendar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const inputCls =
  "w-full bg-transparent border-b border-[#F4EFE6]/25 focus:border-[#D2703F] text-[#F4EFE6] placeholder:text-[#F4EFE6]/40 py-3 outline-none transition-colors duration-300";

const Contact = () => {
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [date, setDate] = useState(null);
  const [form, setForm] = useState({
    name: "",
    email: "",
    experience: "",
    travelers: "",
    message: "",
  });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast({ title: "Almost there", description: "Please add your name and email so we can reply." });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/bookings`, {
        name: form.name,
        email: form.email,
        experience: form.experience || null,
        travelers: form.travelers || null,
        trip_date: date ? format(date, "yyyy-MM-dd") : null,
        message: form.message || null,
      });
      toast({
        title: "Enquiry sent — salamat!",
        description: `Thanks ${form.name.split(" ")[0]}, we'll reply to ${form.email} within a day.`,
      });
      setForm({ name: "", email: "", experience: "", travelers: "", message: "" });
      setDate(null);
    } catch (err) {
      toast({
        title: "Something went wrong",
        description: "We couldn't send your enquiry just now — please try again in a moment.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const contactItems = [
    { icon: Mail, label: CONTACT.email, href: `mailto:${CONTACT.email}` },
    { icon: Phone, label: CONTACT.phone, href: CONTACT.phoneHref },
    { icon: Instagram, label: CONTACT.instagram, href: CONTACT.instagramHref },
    { icon: Facebook, label: CONTACT.facebook, href: CONTACT.facebookHref },
  ];

  return (
    <section id="contact" className="bg-[#1C2621] py-24 md:py-32 scroll-mt-20">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-2 gap-16">
          {/* Left */}
          <div>
            <Reveal className="flex items-center gap-4 mb-6">
              <span className="h-px w-10 bg-[#D2703F]" />
              <span className="eyebrow text-[#D2703F]">Get in touch</span>
            </Reveal>
            <Reveal>
              <h2 className="font-serif-display italic text-4xl md:text-5xl lg:text-[3.4rem] leading-tight text-[#F4EFE6]">
                Let's build your SIQ Adventure
              </h2>
            </Reveal>
            <Reveal delay={100}>
              <p className="mt-6 text-[#F4EFE6]/70 text-lg leading-relaxed max-w-md">
                Whether you're looking for Siquijor tours, things to do, local experiences, beaches, waterfalls or travel tips, we're here to help. We reply to every message ourselves, usually within a day.
              </p>
            </Reveal>
            <Reveal delay={180}>
              <div className="mt-10 space-y-4">
                {contactItems.map((c) => (
                  <a key={c.label} href={c.href} target="_blank" rel="noreferrer" className="group flex items-center gap-4 text-[#F4EFE6]/85 hover:text-[#F4EFE6] transition-colors">
                    <span className="grid place-items-center w-11 h-11 rounded-full bg-[#F4EFE6]/8 group-hover:bg-[#D2703F] transition-colors duration-300">
                      <c.icon className="w-4 h-4" />
                    </span>
                    <span>{c.label}</span>
                  </a>
                ))}
                <div className="flex items-center gap-4 text-[#F4EFE6]/60 pt-2">
                  <span className="grid place-items-center w-11 h-11 rounded-full bg-[#F4EFE6]/8">
                    <MapPin className="w-4 h-4" />
                  </span>
                  <span>{CONTACT.location}</span>
                </div>
              </div>
            </Reveal>
          </div>

          {/* Form */}
          <Reveal delay={120}>
            <form onSubmit={submit} className="bg-[#F4EFE6]/[0.04] border border-[#F4EFE6]/10 rounded-2xl p-8 md:p-10">
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className="eyebrow text-[#F4EFE6]/50 text-[0.62rem]">Your name</label>
                  <input value={form.name} onChange={set("name")} placeholder="Juan Dela Cruz" className={inputCls} />
                </div>
                <div>
                  <label className="eyebrow text-[#F4EFE6]/50 text-[0.62rem]">Email</label>
                  <input type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" className={inputCls} />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-6 mt-6">
                <div>
                  <label className="eyebrow text-[#F4EFE6]/50 text-[0.62rem] block mb-2">Experience</label>
                  <Select value={form.experience} onValueChange={(v) => setForm({ ...form, experience: v })}>
                    <SelectTrigger className="w-full bg-transparent border-0 border-b border-[#F4EFE6]/25 rounded-none px-0 text-[#F4EFE6] focus:ring-0 focus:border-[#D2703F] data-[placeholder]:text-[#F4EFE6]/40">
                      <SelectValue placeholder="Choose one…" />
                    </SelectTrigger>
                    <SelectContent>
                      {CONTACT.experienceOptions.map((o) => (
                        <SelectItem key={o} value={o}>{o}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="eyebrow text-[#F4EFE6]/50 text-[0.62rem]">Travelers</label>
                  <input value={form.travelers} onChange={set("travelers")} placeholder="2 adults" className={inputCls} />
                </div>
              </div>

              <div className="mt-6">
                <label className="eyebrow text-[#F4EFE6]/50 text-[0.62rem] block mb-2">Preferred trip date</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="w-full flex items-center justify-between border-b border-[#F4EFE6]/25 hover:border-[#D2703F] py-3 text-left transition-colors duration-300"
                    >
                      <span className={date ? "text-[#F4EFE6]" : "text-[#F4EFE6]/40"}>
                        {date ? format(date, "EEEE, d MMMM yyyy") : "Pick a date"}
                      </span>
                      <CalendarIcon className="w-4 h-4 text-[#F4EFE6]/60" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={setDate}
                      disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="mt-6">
                <label className="eyebrow text-[#F4EFE6]/50 text-[0.62rem]">Tell us about your trip</label>
                <textarea value={form.message} onChange={set("message")} rows={3} placeholder="What are you dreaming of?" className={`${inputCls} resize-none`} />
              </div>

              <button type="submit" disabled={submitting} className="group mt-8 w-full inline-flex items-center justify-center gap-2.5 rounded-full bg-[#D2703F] hover:bg-[#B85A2C] disabled:opacity-70 text-white font-medium py-4 transition-all duration-300 hover:-translate-y-0.5 disabled:hover:translate-y-0">
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Sending…
                  </>
                ) : (
                  <>
                    Send enquiry
                    <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </>
                )}
              </button>
              <p className="text-center text-[#F4EFE6]/40 text-xs mt-4">Goes straight to {CONTACT.email}</p>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Contact;
